import { useEffect, useMemo, useState } from "react";
import { Download, FileJson, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { saveDownload } from "@/lib/download";
import type {
  CatalogueTemplateBase,
  TemplateExportDownload,
  TemplateImportPreview,
  TemplateImportSource,
  TemplateImportTarget,
} from "@/services/catalogue-template.service";

export type TransferMode = "export" | "import";
interface ConfigurationTransferDialogProps {
  open: boolean;
  mode: TransferMode;
  selected: CatalogueTemplateBase | null;
  templates: CatalogueTemplateBase[];
  onOpenChange: (open: boolean) => void;
  onExport: () => Promise<TemplateExportDownload>;
  onPreview: (source: TemplateImportSource) => Promise<TemplateImportPreview>;
  onImport: (
    source: TemplateImportSource,
    target: TemplateImportTarget
  ) => Promise<CatalogueTemplateBase>;
  onImported: (templateId: string) => Promise<void> | void;
}

function errorText(error: string | { path?: string; message?: string }): string {
  if (typeof error === "string") return error;
  return [error.path, error.message].filter(Boolean).join(": ") || "Invalid import data";
}

function summaryRows(
  summary: Record<string, unknown> | null,
  prefix = ""
): Array<{ label: string; value: string }> {
  if (!summary) return [];
  return Object.entries(summary).flatMap(([key, value]) => {
    const label = [prefix, key].filter(Boolean).join(" · ").replace(/_/g, " ");
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return summaryRows(value as Record<string, unknown>, label);
    }
    const rendered = Array.isArray(value)
      ? value.map(String).join(", ")
      : value == null
        ? "—"
        : String(value);
    return [{ label, value: rendered }];
  });
}

export function ConfigurationTransferDialog({
  open,
  mode,
  selected,
  templates,
  onOpenChange,
  onExport,
  onPreview,
  onImport,
  onImported,
}: ConfigurationTransferDialogProps) {
  const [source, setSource] = useState<TemplateImportSource | null>(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<TemplateImportPreview | null>(null);
  const [targetMode, setTargetMode] = useState<"create" | "replace">("create");
  const [replaceId, setReplaceId] = useState("");
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drafts = useMemo(
    () => templates.filter((template) => template.status === "draft"),
    [templates]
  );

  useEffect(() => {
    if (!open) return;
    setSource(null);
    setFileName("");
    setPreview(null);
    setTargetMode("create");
    setReplaceId(selected?.status === "draft" ? selected.id : drafts[0]?.id ?? "");
    setName("");
    setVersion("");
    setDescription("");
    setIsDefault(false);
    setBusy(false);
    setError(null);
  }, [drafts, open, selected]);

  async function chooseFile(file: File | null) {
    setSource(null);
    setFileName(file?.name ?? "");
    setPreview(null);
    setError(null);
    if (!file) return;

    try {
      const extension = file.name.toLowerCase();
      if (!extension.endsWith(".json")) {
        throw new Error("Choose a JSON categories and attributes export.");
      }
      const nextSource = JSON.parse(await file.text()) as TemplateImportSource;
      setSource(nextSource);
      setBusy(true);
      const result = await onPreview(nextSource);
      setPreview(result);
      if (!result.valid) setError("This file cannot be imported until its validation errors are fixed.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read or validate this file");
    } finally {
      setBusy(false);
    }
  }

  async function exportSelected() {
    if (!selected) return;
    if (selected.status === "archived") {
      setError("Archived configurations cannot be exported. Clone it to a draft first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      saveDownload(await onExport());
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  async function importSelected() {
    if (!source || !preview?.valid) return;
    let target: TemplateImportTarget;
    if (targetMode === "replace") {
      if (!replaceId) {
        setError("Select an editable draft to replace.");
        return;
      }
      target = { mode: "replace", template_id: replaceId };
    } else {
      if (!name.trim() || !version.trim()) {
        setError("Name and version are required for a new draft.");
        return;
      }
      target = {
        mode: "create",
        name: name.trim(),
        version: version.trim(),
        description: description.trim() || null,
        is_default: isDefault,
      };
    }

    setBusy(true);
    setError(null);
    try {
      const imported = await onImport(source, target);
      await onImported(imported.id);
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  const titlePrefix = "Categories and attributes";
  const previewRows = summaryRows(preview?.summary ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === "export" ? `Export ${titlePrefix}` : `Import ${titlePrefix}`}</DialogTitle>
          <DialogDescription>
            {mode === "export"
              ? "Download a portable configuration file without tenant IDs, database IDs, or lifecycle state."
              : "Validate a portable configuration before creating or exactly replacing a draft."}
          </DialogDescription>
        </DialogHeader>

        {mode === "export" ? (
          <div className="space-y-4 py-2">
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <p className="font-medium">{selected?.name ?? "No configuration selected"}</p>
              {selected && <p className="text-muted-foreground">Version {selected.version}</p>}
            </div>
            {selected?.status === "archived" && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Archived configurations cannot be exported. Clone this configuration to a draft first.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed p-6 text-sm hover:bg-muted/50">
              <FileJson className="h-5 w-5 text-muted-foreground" />
              <span>{fileName || "Choose a JSON categories and attributes file"}</span>
              <input
                type="file"
                className="sr-only"
                accept=".json,application/json"
                onChange={(event) => void chooseFile(event.target.files?.[0] ?? null)}
              />
            </label>

            {preview && (
              <div className="rounded-md border p-3 text-sm">
                <p className="font-medium">{preview.valid ? "Validation passed" : "Validation failed"}</p>
                {previewRows.length > 0 && (
                  <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                    {previewRows.map(({ label, value }) => (
                      <div key={label} className="rounded bg-muted/50 px-2 py-1.5">
                        <dt className="text-xs capitalize text-muted-foreground">{label}</dt>
                        <dd className="font-medium">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {preview.errors.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-red-700">
                    {preview.errors.map((item, index) => <li key={index}>{errorText(item)}</li>)}
                  </ul>
                )}
              </div>
            )}

            {preview?.valid && (
              <div className="space-y-4 rounded-md border p-4">
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={targetMode === "create"}
                      onChange={() => setTargetMode("create")}
                    />
                    Create a new draft
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={targetMode === "replace"}
                      disabled={drafts.length === 0}
                      onChange={() => setTargetMode("replace")}
                    />
                    Replace an existing draft
                  </label>
                </div>
                {targetMode === "replace" ? (
                  <label className="grid gap-2">
                    <span className="text-sm font-medium">Draft to replace</span>
                    <select
                      value={replaceId}
                      onChange={(event) => setReplaceId(event.target.value)}
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                    >
                      {drafts.map((draft) => (
                        <option key={draft.id} value={draft.id}>{draft.name} · {draft.version}</option>
                      ))}
                    </select>
                    <span className="text-xs text-muted-foreground">
                      The draft content is replaced exactly. Published configurations cannot be changed.
                    </span>
                  </label>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="configuration-import-name">Name</Label>
                      <Input id="configuration-import-name" value={name} onChange={(event) => setName(event.target.value)} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="configuration-import-version">Version</Label>
                      <Input id="configuration-import-version" value={version} onChange={(event) => setVersion(event.target.value)} />
                    </div>
                    <div className="grid gap-2 sm:col-span-2">
                      <Label htmlFor="configuration-import-description">Description</Label>
                      <Textarea id="configuration-import-description" value={description} onChange={(event) => setDescription(event.target.value)} />
                    </div>
                    <label className="flex items-center gap-2 text-sm sm:col-span-2">
                      <Checkbox checked={isDefault} onCheckedChange={(checked) => setIsDefault(checked === true)} />
                      Mark as default when published
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {error && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            disabled={
              busy ||
              (mode === "export" ? !selected || selected.status === "archived" : !preview?.valid)
            }
            onClick={() => void (mode === "export" ? exportSelected() : importSelected())}
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : mode === "export" ? <Download className="mr-1 h-4 w-4" /> : <Upload className="mr-1 h-4 w-4" />}
            {busy ? "Working…" : mode === "export" ? "Export" : "Import draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
